import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import loadable from '@loadable/component';

import { types as sdkTypes } from '../../util/sdkLoader';
import { logout, authenticationInProgress } from '../../ducks/auth.duck';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  fetchCurrentUserNotifications,
  hasCurrentUserErrors,
  sendVerificationEmail,
} from '../../ducks/user.duck';
import { transitions } from '../../transactions/transactionProcessAcceptBid';
import { sendReview } from '../TransactionPage/TransactionPage.duck';

const { UUID } = sdkTypes;

const Topbar = loadable(() => import(/* webpackChunkName: "Topbar" */ './Topbar/Topbar'));

/**
 * Topbar container component, which is connected to Redux Store.
 * @component
 * @param {Object} props
 * @param {number} props.notificationCount number of notifications
 * @param {Function} props.onLogout logout function
 * @param {Function} props.onManageDisableScrolling manage disable scrolling function
 * @param {Function} props.onResendVerificationEmail resend verification email function
 * @param {Object} props.sendVerificationEmailInProgress send verification email in progress
 * @param {Object} props.sendVerificationEmailError send verification email error
 * @param {boolean} props.hasGenericError has generic error
 * @returns {JSX.Element}
 */
export const TopbarContainerComponent = props => {
  const { notificationCount = 0, hasGenericError, ...rest } = props;

  return (
    <Topbar notificationCount={notificationCount} showGenericError={hasGenericError} {...rest} />
  );
};

const mapStateToProps = state => {
  // Topbar needs isAuthenticated and isLoggedInAs
  const { isAuthenticated, isLoggedInAs, logoutError, authScopes } = state.auth;
  // Topbar needs user info.
  const {
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    currentUserSaleNotificationCount = 0,
    currentUserOrderNotificationCount = 0,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    reviewTransactionRefs,
  } = state.user;
  const {
    sendReviewInProgress,
    sendReviewError,
  } = state.TransactionPage;
  const hasGenericError = !!(logoutError || hasCurrentUserErrors(state));
  const { businessListingId } = (currentUser && currentUser.id && currentUser.attributes.profile.protectedData) || {};
  const ref = { id: new UUID(businessListingId), type: 'ownListing' };
  const businessListings = getMarketplaceEntities(state, [ref]);
  const businessListing = (Array.isArray(businessListings) && businessListings.length) ? businessListings[0] : null;
  const reviewTransactions = getMarketplaceEntities(state, reviewTransactionRefs);
  const filteredReviewTransactions = reviewTransactions.length && currentUser && currentUser.id
    ? reviewTransactions.filter(transaction => {
      const { lastTransition } = transaction.attributes || {};

      if (currentUser.id.uuid == transaction.provider.id.uuid) {
        return [transitions.OPERATOR_COMPLETE, transitions.REVIEW_1_BY_CUSTOMER].includes(lastTransition);
      } else if (currentUser.id.uuid == transaction.customer.id.uuid) {
        return [transitions.OPERATOR_COMPLETE, transitions.REVIEW_1_BY_PROVIDER].includes(lastTransition);
      }
      return false;
    })
    : [];

  return {
    authInProgress: authenticationInProgress(state),
    currentUser,
    businessListing,
    currentUserHasListings,
    currentUserHasOrders,
    sendReviewInProgress,
    sendReviewError,
    reviewTransactions: filteredReviewTransactions,
    notificationCount: currentUserSaleNotificationCount + currentUserOrderNotificationCount,
    isAuthenticated,
    isLoggedInAs,
    authScopes,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    hasGenericError,
  };
};

const mapDispatchToProps = dispatch => ({
  onLogout: historyPush => dispatch(logout(historyPush)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onResendVerificationEmail: () => dispatch(sendVerificationEmail()),
  onFetchCurrentUserNotifications: () => dispatch(fetchCurrentUserNotifications()),
  onSendReview: (tx, transitionOptions, params, config) => dispatch(sendReview(tx, transitionOptions, params, config)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const TopbarContainer = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(TopbarContainerComponent);

export default TopbarContainer;
